import { type ReactNode, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button as TamaguiButton, XStack } from "tamagui";
import { useNavigate } from "react-router-dom";

import { KindredLogoButton, MemberHeaderActions } from "@/components/MemberHeaderControls";

import {
  AppContentContainer,
  AppHeroShell,
  AppPage,
  AppShell,
  AppShellMain,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type MemberPageScaffoldProps = {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  fallbackPath?: string;
  titleLines?: number;
  onTitlePress?: () => void;
  titleActionLabel?: string;
  stickyHeader?: boolean;
  children: ReactNode;
};

type MemberPillTone = "neutral" | "primary" | "secondary" | "success" | "warning" | "danger";

type MemberPillProps = {
  tone?: MemberPillTone;
  children: ReactNode;
};

const PILL_TONES: Record<MemberPillTone, { backgroundColor: string; borderColor: string; color: string }> = {
  neutral: {
    backgroundColor: "$background",
    borderColor: "$borderColor",
    color: "$colorMuted",
  },
  primary: {
    backgroundColor: "$primarySoft",
    borderColor: "$primarySoft",
    color: "$primary",
  },
  secondary: {
    backgroundColor: "$secondarySoft",
    borderColor: "$secondarySoft",
    color: "$secondary",
  },
  success: {
    backgroundColor: "$accentSoft",
    borderColor: "$success",
    color: "$success",
  },
  warning: {
    backgroundColor: "$accentSoft",
    borderColor: "$accent",
    color: "$accent",
  },
  danger: {
    backgroundColor: "$primarySoft",
    borderColor: "$destructive",
    color: "$destructive",
  },
};

export function MemberPageScaffold({
  title,
  subtitle,
  action,
  fallbackPath = "/kinfeed",
  titleLines = 1,
  onTitlePress,
  titleActionLabel,
  stickyHeader = false,
  children,
}: MemberPageScaffoldProps) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallbackPath);
  };

  return (
    <AppShell minHeight="100vh">
      <header className={`${stickyHeader ? "sticky top-0" : "relative"} z-20 bg-background/95 pb-1 backdrop-blur-sm`}>
        <AppContentContainer pt="$xs">
          <AppHeroShell
            title={title}
            titleLines={titleLines}
            onTitlePress={onTitlePress}
            titleActionLabel={titleActionLabel}
            leading={<KindredLogoButton />}
            backAction={
              <TamaguiButton
                unstyled
                type="button"
                width={40}
                height={40}
                alignItems="center"
                justifyContent="center"
                borderWidth={1}
                borderRadius="$pill"
                borderColor="$borderColor"
                backgroundColor="$cardBackground"
                pressStyle={{ opacity: 0.92 }}
                onPress={handleBack}
                aria-label="Go back"
              >
                <ArrowLeft size={18} className="text-primary" />
              </TamaguiButton>
            }
            action={<MemberHeaderActions>{action}</MemberHeaderActions>}
          />
        </AppContentContainer>
      </header>

      <AppShellMain>
        <AppPage tag="main" gap="$md" maxWidth={960}>
          {subtitle ? (
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              {subtitle}
            </AppText>
          ) : null}
          {children}
        </AppPage>
      </AppShellMain>
    </AppShell>
  );
}

export function MemberPill({ tone = "neutral", children }: MemberPillProps) {
  const palette = PILL_TONES[tone];

  return (
    <XStack
      alignItems="center"
      justifyContent="center"
      borderWidth={1}
      borderRadius="$pill"
      px="$sm"
      py={5}
      backgroundColor={palette.backgroundColor}
      borderColor={palette.borderColor}
    >
      <AppText
        color={palette.color}
        fontFamily={kindredFontFamilies.sansBold}
        fontSize="$xs"
        lineHeight="$xs"
      >
        {children}
      </AppText>
    </XStack>
  );
}
